import { Box, Button, Dialog, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import { fetchOrder } from 'entities/Order/model/services/fetchOrder/fetchOrder';
import { getSelectedPropertyCatalogId } from 'entities/Property/model/selectors/PropertySelectors';
import { CatalogItemCategoryFormBody } from 'features/AddCatalogItem/ui/AddCatalogItem/CatalogItemCategoryFormBody';
// eslint-disable-next-line max-len
import { changeCatalogItemCategoryService } from 'features/changeCatalogItemCategory/model/services/changeCatalogItemCategoryService';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { DialogBoxContent } from 'shared/ui/DialogBoxContent/DialogBoxContent';
import { Loader } from 'shared/ui/Loader/Loader';

interface FormValues {
    category: string;
}

interface ChangeCatalogItemCategoryDialogProps {
    itemId: string;
    itemName: string;
    categoryId: string;
    isUncategorized: boolean;
    onClose: () => void;
}

export const ChangeCatalogItemCategoryDialog = ({
    itemId,
    itemName,
    categoryId,
    isUncategorized,
    onClose,
}: ChangeCatalogItemCategoryDialogProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const catalogId = useSelector(getSelectedPropertyCatalogId);
    const orderId = useSelector(getSelectedOrderId);

    const title = isUncategorized ? t('Add category') : t('Edit category');

    const handleSubmit = async (values: FormValues): Promise<void> => {
        await dispatch(
            changeCatalogItemCategoryService({
                catalogId,
                itemId,
                categoryId: values.category,
            }),
        ).unwrap();
        await dispatch(fetchOrder({ id: orderId }));
        onClose();
    };

    return (
        <Dialog open onClose={onClose}>
            <Formik<FormValues>
                initialValues={{ category: isUncategorized ? '' : categoryId }}
                onSubmit={handleSubmit}
            >
                {(props: FormikProps<FormValues>) => (
                    <Form>
                        <DialogBoxContent
                            onClose={onClose}
                            buttons={
                                <Box
                                    data-testid="change-catalog-item-category-actions-block"
                                    display="flex"
                                    justifyContent="flex-end"
                                    width="100%"
                                    gap="8px"
                                >
                                    <Button onClick={onClose}>
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        disabled={
                                            !props.values.category ||
                                            props.isSubmitting
                                        }
                                        startIcon={
                                            props.isSubmitting && (
                                                <Loader color="inherit" />
                                            )
                                        }
                                        variant="contained"
                                        type="submit"
                                    >
                                        {t('Save')}
                                    </Button>
                                </Box>
                            }
                        >
                            <Typography
                                data-testid="change-catalog-item-category-title"
                                typography={'poppins.subtitle2'}
                                mb="16px"
                            >
                                {title}
                            </Typography>
                            <Typography
                                data-testid="change-catalog-item-category-item-name"
                                typography="openSans.body2"
                                mb="36px"
                                sx={{ opacity: 0.6 }}
                            >
                                {itemName}
                            </Typography>
                            <CatalogItemCategoryFormBody />
                        </DialogBoxContent>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};
